/**
 * The government agency's dashboard.
 *
 * Budget utilisation, reach by region, and what is actually stopping businesses
 * from qualifying. The last of those is the only one that changes policy, which
 * is why it is a table of remediable and non-remediable constraints rather than
 * a decline rate.
 *
 * Small cells are suppressed. An agency view over a thin region is a view over
 * identifiable businesses, and the minimum cell size the server applies is the
 * difference between a statistic and a disclosure.
 */
import { Link } from 'react-router-dom'

import type { Dict, Grant, SectionMeta } from '../../api/types'
import { useSession } from '../../app/session'
import { appPath } from '../../app/paths'
import { Badge, Empty, Meter, Note } from '../../components/bits'
import { PageHead } from '../../components/PageHead'
import { Panel, Cols } from '../../components/Panel'
import { Bars, TileStrip } from './common'
import type { TileData } from './common'

export function AgencyDashboard({ data, section, grant }: {
  data: Dict; section: SectionMeta; grant?: Grant
}) {
  const { n, amount, d, lbl } = useSession()
  const schemes = (data.schemes || []) as Dict[]
  const reach = (data.reach || []) as Dict[]
  const constraints = (data.constraints || []) as Dict[]
  const outcomes = (data.outcomes || []) as Dict[]
  const minCell = Number(data.min_cell_size) || 0

  const remediable = constraints.filter((c) => c.is_remediable)
  const structural = constraints.filter((c) => !c.is_remediable)

  return (
    <>
      <PageHead section={section} grant={grant} />
      <TileStrip tiles={(data.tiles || []) as TileData[]} />

      <Panel title={`Schemes · ${schemes.length}`} explain="scheme.register" tight
             actions={<Link className="btn sm" to={appPath('/app/schemes')}>
               Open the register</Link>}>
        {schemes.length === 0 ? (
          <Empty title="No scheme is open" />
        ) : (
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  <th>Scheme</th><th>Instrument</th><th className="num">Budget</th>
                  <th className="num">Committed</th><th className="num">Disbursed</th>
                  <th className="num">Used</th><th>Window</th><th>State</th>
                </tr>
              </thead>
              <tbody>
                {schemes.map((s) => (
                  <tr key={String(s.scheme_id)}>
                    <td data-label="Scheme">{s.name_en as string}</td>
                    <td data-label="Instrument">
                      <Badge tone="muted">{lbl(s.instrument)}</Badge>
                    </td>
                    <td data-label="Budget" className="num">
                      {amount(s.budget_total, null, 0)}
                    </td>
                    <td data-label="Committed" className="num">
                      {amount(s.budget_committed, null, 0)}
                    </td>
                    <td data-label="Disbursed" className="num">
                      {amount(s.budget_disbursed, null, 0)}
                    </td>
                    <td data-label="Used" className="num">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7,
                                    justifyContent: 'flex-end' }}>
                        <span>{n(s.utilisation_pct, 1)}%</span>
                        <div style={{ width: 54 }}>
                          <Meter pct={Number(s.utilisation_pct)} />
                        </div>
                      </div>
                    </td>
                    <td data-label="Window">
                      {s.opens_at ? d(s.opens_at) : '—'}
                      {s.closes_at ? ` to ${d(s.closes_at)}` : ''}
                    </td>
                    <td data-label="State">
                      <Badge tone={s.status === 'open' ? 'good' : 'muted'}>
                        {lbl(s.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="xs muted">
          Committed and disbursed are different facts and are never added
          together. A guarantee committed against a facility that is never drawn
          costs the scheme nothing, and reporting the two as one figure overstates
          deployment.
        </p>
      </Panel>

      <Cols>
        <Panel title="Reach by region" explain="analytics.reach">
          {reach.length === 0 ? <Empty title="Nothing to report yet" /> : (
            <Bars rows={reach} labelKey="region" valueKey="customers" />
          )}
          <p className="xs muted">
            Businesses on the platform, by headquarters region. A region with
            fewer than {n(minCell)} is suppressed rather than rounded, because a
            count of two in a named region with a named activity is a business
            somebody can identify.
          </p>
        </Panel>

        <Panel title="Outcomes" explain="analytics.outcomes">
          {outcomes.length === 0 ? (
            <Empty title="No outcome has been recorded in the period" />
          ) : (
            <Bars rows={outcomes} labelKey="k" valueKey="n" />
          )}
        </Panel>
      </Cols>

      <Panel title="What is actually stopping businesses" explain="analytics.constraints" tight>
        {constraints.length === 0 ? (
          <Empty title="No constraint has been recorded" />
        ) : (
          <>
            <Note kind="info">
              <b>{n(remediable.length)} of {n(constraints.length)} constraint
              types are remediable.</b> A remediable constraint has a stated
              action and a typical time to fix, which is what turns a decline
              into a programme. The rest are structural, and a programme aimed at
              them is a different instrument.
            </Note>
            <div className="tbl-wrap">
              <table className="dl">
                <thead>
                  <tr>
                    <th>Constraint</th><th>Category</th>
                    <th className="num">Businesses</th><th>Remediable</th>
                    <th className="num">Typical months to fix</th>
                  </tr>
                </thead>
                <tbody>
                  {[...remediable, ...structural].map((c, i) => (
                    <tr key={String(c.decline_code || c.k || i)}>
                      <td data-label="Constraint">{c.name_en as string}</td>
                      <td data-label="Category">
                        <Badge tone="muted">{lbl(c.category)}</Badge>
                      </td>
                      <td data-label="Businesses" className="num">{n(c.n)}</td>
                      <td data-label="Remediable">
                        <Badge tone={c.is_remediable ? 'good' : 'bad'}>
                          {c.is_remediable ? 'yes' : 'structural'}
                        </Badge>
                      </td>
                      <td data-label="Typical months to fix" className="num">
                        {c.typical_months_to_fix === null
                          || c.typical_months_to_fix === undefined
                          ? '—' : n(c.typical_months_to_fix, 1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>

      <Panel title="What an agency cannot see here" explain={section.section_key}>
        <p className="muted">
          No business is named on this page, and no row describes one. An agency
          role holds aggregate row scope, which is applied as a predicate in the
          query rather than as a choice made by this screen: a view that depended
          on the front end not to draw a name would leak the first time somebody
          added a column.
        </p>
      </Panel>
    </>
  )
}
